import { clsx } from 'clsx'
import { useState } from 'react'
import { CopyButton } from '@/components/ui/copy-button'

const PLATFORMS = ['Elementor', 'CF7 / WPForms', 'Any HTML', 'Any Platform'] as const
type PlatformType = (typeof PLATFORMS)[number]

function NumberedStep({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <li className="list-decimal pl-1 text-sm leading-relaxed text-text-primary">{children}</li>
}

function ElementorInstructions(): React.JSX.Element {
  return (
    <ol className="space-y-1.5 pl-4">
      <NumberedStep>
        Add a <strong>Form</strong> widget to your page
      </NumberedStep>
      <NumberedStep>
        Add an Email field (set the name to{' '}
        <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-xs">email</code>)
      </NumberedStep>
      <NumberedStep>
        Add a Hidden field (set the name to{' '}
        <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-xs">website</code>, leave
        value empty)
      </NumberedStep>
      <NumberedStep>
        Under Form &rarr; Actions After Submit, select <strong>Webhook</strong>
      </NumberedStep>
      <NumberedStep>Paste your endpoint URL</NumberedStep>
      <NumberedStep>
        Set Method to <strong>POST</strong>
      </NumberedStep>
      <NumberedStep>Save and test</NumberedStep>
    </ol>
  )
}

function Cf7WpFormsInstructions(): React.JSX.Element {
  return (
    <ol className="space-y-1.5 pl-4">
      <NumberedStep>Create your form with an email field</NumberedStep>
      <NumberedStep>
        In the form settings, find <strong>Custom Action</strong> or <strong>Webhook</strong>{' '}
        integration
      </NumberedStep>
      <NumberedStep>Paste your endpoint URL</NumberedStep>
      <NumberedStep>
        Map the email field to{' '}
        <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-xs">email</code>
      </NumberedStep>
    </ol>
  )
}

function HtmlFormInstructions({ endpointUrl }: { endpointUrl: string }): React.JSX.Element {
  const htmlSnippet = `<form method="POST" action="${endpointUrl}">
  <input type="email" name="email" placeholder="Your email" required>
  <input type="text" name="website" style="display:none" tabindex="-1">
  <button type="submit">Subscribe</button>
</form>`

  return (
    <div className="space-y-3">
      <p className="text-xs text-text-secondary">
        Copy this and style it however you want &mdash; the form action does the rest.
      </p>
      <div className="relative rounded-lg bg-surface-1 p-3">
        <pre className="overflow-x-auto font-mono text-xs leading-relaxed text-text-primary whitespace-pre">
          {htmlSnippet}
        </pre>
        <div className="absolute top-2 right-2">
          <CopyButton value={htmlSnippet} />
        </div>
      </div>
    </div>
  )
}

function AnyPlatformInstructions(): React.JSX.Element {
  return (
    <p className="text-sm text-text-primary leading-relaxed">
      Any form that can POST to a URL works &mdash; Webflow, Squarespace, Carrd, or any other
      platform. Set the form action to your endpoint URL and include an email field named{' '}
      <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-xs">email</code>.
    </p>
  )
}

interface Props {
  endpointUrl: string
}

export function FormConnectionTabs({ endpointUrl }: Props): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<PlatformType>('Elementor')

  return (
    <div className="rounded-lg border border-surface-3 bg-surface-0">
      <div className="flex border-b border-surface-3">
        {PLATFORMS.map((platform) => (
          <button
            key={platform}
            type="button"
            onClick={() => setActiveTab(platform)}
            className={clsx(
              'flex-1 px-3 py-2 text-xs font-medium transition-colors',
              activeTab === platform
                ? 'border-b-2 border-brand-500 text-brand-600'
                : 'text-text-tertiary hover:text-text-secondary',
            )}
          >
            {platform}
          </button>
        ))}
      </div>
      <div className="p-4">
        {activeTab === 'Elementor' && <ElementorInstructions />}
        {activeTab === 'CF7 / WPForms' && <Cf7WpFormsInstructions />}
        {activeTab === 'Any HTML' && <HtmlFormInstructions endpointUrl={endpointUrl} />}
        {activeTab === 'Any Platform' && <AnyPlatformInstructions />}
      </div>
    </div>
  )
}
